import React, { CSSProperties, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import ReactDatePicker from "react-datepicker";
import styled from "styled-components";
import { scrollbars } from "theme/mixins";

import { useRootElement } from "ui/hooks";

import { Button } from "minimum-ui/core";
import { MenuWrapper } from "../Filter/Menu";
import CustomHeader from "./CustomHeader";
import DatePickerStyles from "./Styles";

export interface MenuProps {
  open: boolean;
  onClose: () => void;
  onApply: (dates: [Date | null, Date | null]) => void;
  top: number;
  left: number;
  right: number;
  rightAligned: boolean;
  startDate: Date | null | undefined;
  endDate: Date | null | undefined;
  translate: (
    key: string,
    options?: Record<string, string | number | boolean>
  ) => string;
}

const Menu: React.FC<MenuProps> = ({
  open,
  onClose,
  onApply,
  top,
  left,
  right,
  rightAligned = false,
  startDate = null,
  endDate = null,
  translate,
}) => {
  const portal = useRootElement("date-picker");

  const [startValue, setStartValue] = useState<Date | null>(startDate);
  const [endValue, setEndValue] = useState<Date | null>(endDate);
  const [datesChanged, setDatesChanged] = useState(false);

  // Keep our state in-sync with provided value
  useEffect(() => {
    setStartValue(startDate);
    setEndValue(endDate);
    setDatesChanged(false);
  }, [startDate, endDate]);

  const handleChange = useCallback((dates: [Date | null, Date | null]) => {
    setStartValue(dates[0]);
    setEndValue(dates[1]);
    if (dates[0] && dates[1]) {
      setDatesChanged(true);
    }
  }, []);

  const onCancel = useCallback(() => {
    onApply([null, null]);
    setStartValue(null);
    setEndValue(null);
    setDatesChanged(false);
    onClose();
  }, [onApply, onClose]);

  const handleApply = useCallback(() => {
    onApply([startValue, endValue]);
    onClose();
  }, [onApply, startValue, endValue, onClose]);

  const months = translate("months", {
    returnObjects: true,
    namespace: "DEFAULT",
  }) as unknown as string[];

  return createPortal(
    <>
      {open && <Backdrop onClick={onClose} />}
      <DatePicker
        $open={open}
        $right={rightAligned}
        $showReset={false}
        $showApply={false}
        role="dialog"
        style={
          {
            "--top": `${top}px`,
            "--left": `${left}px`,
            "--right": `${right}px`,
          } as CSSProperties
        }
      >
        <DatePickerStyles />
        <ReactDatePicker
          onChange={handleChange}
          selectsRange={true}
          monthsShown={1}
          inline={true}
          startDate={startValue}
          endDate={endValue}
          calendarStartDay={1}
          fixedHeight={true}
          renderCustomHeader={CustomHeader(months)}
        />
        <ButtonContainer>
          <DatePickerButton
            size="large"
            variant="outlined"
            onClick={onCancel}
            disabled={!startValue && !endValue}
          >
            {translate("reset", { namespace: "DEFAULT" })}
          </DatePickerButton>
          <DatePickerButton
            size="large"
            variant="contained"
            disabled={!datesChanged}
            onClick={handleApply}
          >
            {translate("apply", { namespace: "DEFAULT" })}
          </DatePickerButton>
        </ButtonContainer>
      </DatePicker>
    </>,
    portal
  );
};

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 100;
`;

const DatePicker = styled(MenuWrapper)`
  // width: 656px;
  padding: 0;
  overflow-y: auto;
  ${scrollbars()}
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin: 0 1rem 1rem 0;
`;

const DatePickerButton = styled(Button)`
  margin-left: 16px;
`;

export default Menu;
